import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useProduct } from '@/hooks/api/useProducts';
import { useMockAuth } from '@/hooks/useMockAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loading } from '@/components/ui/spinner';
import { ArrowLeft } from 'lucide-react';

const ProductDetail = () => {
  const navigate = useNavigate();
  const { productId } = useParams({ from: '/marketplace/$productId' });
  const { data: product, isLoading, error } = useProduct(productId);
  const { isAuthenticated } = useMockAuth();

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const handlePurchase = () => {
    if (!isAuthenticated) {
      alert('Sign in required to purchase. Redirecting to login...');
      navigate({ to: '/login' });
    } else {
      // TODO: Implement purchase flow (Stripe checkout)
      alert('Purchase flow not yet implemented');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading text="Loading product..." />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-background">
        <nav className="border-b bg-card">
          <div className="container mx-auto px-4 py-4">
            <Button variant="ghost" asChild>
              <Link to="/marketplace">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Marketplace
              </Link>
            </Button>
          </div>
        </nav>
        <div className="container mx-auto px-4 py-8">
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-sm text-destructive">
                {error instanceof Error ? error.message : 'Product not found'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" asChild>
            <Link to="/marketplace">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Marketplace
            </Link>
          </Button>
          <div className="flex gap-2">
            {!isAuthenticated ? (
              <Button asChild>
                <Link to="/login">Sign In</Link>
              </Button>
            ) : (
              <Button variant="ghost" asChild>
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            )}
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardContent className="p-0">
                <div className="aspect-video bg-muted rounded-t-lg overflow-hidden relative">
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-8xl">🎵</span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="space-y-2">
                      <Badge>{product.category}</Badge>
                      <h1 className="text-3xl font-bold">{product.name}</h1>
                    </div>
                  </div>
                  <p className="text-muted-foreground text-lg mb-6">
                    {product.description}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <div className="text-4xl font-bold mb-2">
                  {formatPrice(product.price)}
                </div>
                <CardDescription>
                  Status: {product.status === 'active' ? (
                    <Badge variant="success">Available</Badge>
                  ) : (
                    <Badge variant="destructive">Unavailable</Badge>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  className="w-full"
                  size="lg"
                  onClick={handlePurchase}
                  disabled={product.status !== 'active'}
                >
                  Purchase Now
                </Button>
                <div className="space-y-2 text-sm">
                  <p className="text-muted-foreground">
                    Developer: {product.developerId}
                  </p>
                  <p className="text-muted-foreground">
                    Created: {new Date(product.createdAt).toLocaleDateString()}
                  </p>
                  {product.updatedAt && (
                    <p className="text-muted-foreground">
                      Updated: {new Date(product.updatedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProductDetail;
